import { getValueAndUnit, styled } from '@lessonup/ui-components';
import React, { ReactNode } from 'react';

/**
 * Padding for the text element should be a fraction of the width of a glyph in the font.
 * https://en.wikipedia.org/wiki/Whitespace_character
 */
const THREE_PER_EM_WHITESPACE = 3;

export type TextStyle = 'title' | 'headline' | 'subheadline' | 'small-text';

type PinTextThemeSettings = {
  fontSize: Record<TextStyle, string>;
};

export interface PinTextProps {
  children: ReactNode;
  textStyle: TextStyle;
  className?: string;
}

/**
 * Text that can be used by a teacher inside a pin of a lesson
 *
 * Note: There still is some discussion about the proper name for this component. ComponentText/PinText is what's used in Figma at the moment,
 * but it's called PinText here now to avoid more discussion.
 * The intention is for this to be a more generic component that can be used for other answer areas as well.
 *
 * Note2: Only the properties necessary for the EmojiPoll answers are implemented at the moment.
 */
export const PinText: React.FC<PinTextProps> = ({ children, textStyle, className }) => {
  const theme: PinTextThemeSettings = {
    fontSize: {
      title: '56px',
      headline: '32px',
      subheadline: '24px',
      'small-text': '18px',
    },
  };

  return (
    <StyledPinText fontSize={theme.fontSize[textStyle]} className={className}>
      {children}
    </StyledPinText>
  );
};

const StyledPinText = styled.span<{ fontSize: string }>`
  font-size: ${({ fontSize }) => fontSize};
  ${({ fontSize }) => {
    const [value, unit] = getValueAndUnit(fontSize);
    if (!value || isNaN(value)) return '';
    const paddingValue = value / THREE_PER_EM_WHITESPACE;
    return `padding: 0 ${paddingValue}${unit};`; //TODO: Can't the padding just be (1/3)em?
  }}
`;
